import React, { createContext, useCallback, useState, useContext } from "react";
import api from "../services/api";
import UserRoles from "../enums/UserRoles";

interface User {
  id: string;
  avatar_url: string;
  name: string;
  email: string;
  role: UserRoles;
}

interface SignInCredencials {
  email: string;
  password: string;
}

interface AuthState {
  user: User;
  token: string;
}

interface AuthContextData {
  user: User;
  token: string;
  signIn(credencials: SignInCredencials): Promise<UserRoles>;
  signOut(): void;
  updateUser(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem("@eTI:token");
    const user = localStorage.getItem("@eTI:User");
    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post("sessions", {
      email,
      password,
    });

    const { user, token } = response.data;

    localStorage.setItem("@eTI:token", token);
    localStorage.setItem("@eTI:User", JSON.stringify(user));

    api.defaults.headers.authorization = `Bearer ${token}`;

    setData({
      user,
      token,
    });

    const role = user.role as UserRoles;
    return role;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("@eTI:token");
    localStorage.removeItem("@eTI:User");

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem("@eTI:User", JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        token: data.token,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}
